import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, X, Hourglass, Calculator as CalcIcon, Eye, Scales, Sparkle } from "@phosphor-icons/react";

const TOUR_STEPS = [
  {
    id: 1,
    icon: Sparkle,
    titre: "Bienvenue dans VITA-LEX",
    description: "Vous êtes désormais à la tête d'un dossier vitaliste personnel. Cette plateforme convertit chaque chiffre des finances publiques en jours de votre vie. Trois minutes pour comprendre.",
  },
  {
    id: 2,
    icon: CalcIcon,
    titre: "1 · Mesurez votre situation",
    description: "Le module Vita-Engine applique 11 formules (V1—V7, D1—D5) pour transformer votre fiche de paie et la dette nationale en jours sacrifiés, par citoyen et par an.",
    cta: { label: "Ouvrir le Vita-Engine", route: "/calculator" },
  },
  {
    id: 3,
    icon: Eye,
    titre: "2 · Ingérez les rapports officiels",
    description: "Vita-Observatory ingère un PDF du FMI, de la Banque Mondiale ou de la BAD. L'IA Claude Sonnet 4.5 produit un résumé exécutif, extrait les ratios et déclenche les alertes.",
    cta: { label: "Ouvrir l'Observatory", route: "/observatory" },
  },
  {
    id: 4,
    icon: Hourglass,
    titre: "3 · Simulez les restructurations",
    description: "Les Scénarios chiffrent l'impact d'un Cadre Commun G20 sur 20 ans. L'IRH (Indice de Restitution Humaine) mesure les années humaines collectives restituées au peuple.",
    cta: { label: "Ouvrir Scenarios", route: "/scenarios" },
  },
  {
    id: 5,
    icon: Scales,
    titre: "4 · Rédigez et publiez",
    description: "Le Vita-Writer produit en 8 secondes une Demande de Vérification Citoyenne ou un Rapport de Reddition Comptable. Publiez sous lien anonyme, exportez en PDF officiel.",
    cta: { label: "Ouvrir Vita-Writer", route: "/reports" },
  },
];

export default function TourGuide({ onClose }) {
  const [step, setStep] = useState(0);
  const nav = useNavigate();
  const total = TOUR_STEPS.length;
  const current = TOUR_STEPS[step];
  const Icon = current.icon;

  const finish = async () => {
    try { await api.post("/apprentissage/tour/seen"); } catch { /* non bloquant */ }
    onClose();
  };

  const goCta = async () => {
    if (current.cta?.route) {
      await finish();
      nav(current.cta.route);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="tour-guide">
      <div className="bg-[var(--paper)] border-2 border-[var(--signal)] max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="border-b border-black/15 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-[0.18em] text-[var(--muted)]">§ Tour guidé · {step + 1}/{total}</span>
            <div className="flex gap-1">
              {TOUR_STEPS.map((_, i) => (
                <div key={i} className="w-8 h-1" style={{ background: i <= step ? "var(--signal)" : "rgba(0,0,0,0.15)" }} />
              ))}
            </div>
          </div>
          <button onClick={finish} className="text-[var(--muted)] hover:text-[var(--signal)]" data-testid="tour-skip" title="Passer">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8" data-testid={`tour-step-${current.id}`}>
          <div className="flex items-start gap-4">
            <div className="shrink-0 p-3 border-2 border-[var(--signal)] bg-[var(--signal-light)]">
              <Icon size={32} weight="duotone" className="text-[var(--signal)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-serif text-3xl tracking-tight">{current.titre}</h2>
              <p className="mt-3 leading-relaxed text-[var(--ink)]">{current.description}</p>
              {current.cta && (
                <button onClick={goCta} data-testid="tour-cta"
                  className="mt-5 border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2">
                  {current.cta.label} <ArrowRight size={12} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-black/15 p-4 flex items-center justify-between bg-[var(--paper-2)]">
          <button onClick={finish} className="label-mono text-[var(--muted)] hover:text-[var(--signal)]" data-testid="tour-finish-skip">
            Plus tard
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} data-testid="tour-prev"
                className="border border-black/30 px-4 py-2 label-mono hover:border-black">
                Précédent
              </button>
            )}
            {step < total - 1 ? (
              <button onClick={() => setStep(step + 1)} data-testid="tour-next"
                className="bg-[var(--signal)] text-[var(--paper)] px-5 py-2 label-mono flex items-center gap-2">
                Suivant <ArrowRight size={12} weight="bold" />
              </button>
            ) : (
              <button onClick={finish} data-testid="tour-finish"
                className="bg-[var(--ink)] text-[var(--paper)] px-5 py-2 label-mono flex items-center gap-2">
                Commencer <ArrowRight size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function useTourGuide() {
  const { user } = useAuth();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/apprentissage/progression")
      .then((r) => {
        const seen = !!r.data?.tour_seen;
        if (!seen) setShowTour(true);
      })
      .catch(() => { /* silencieux */ });
  }, [user]);

  return { showTour, closeTour: () => setShowTour(false) };
}
